import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

/**
 * FeaturedSection – live category cards powered by Supabase.
 * Each card shows real counts and navigates to the search/explore page.
 */

const categoryDefs = [
  {
    key: 'tech',
    title: 'Tech Titans',
    description: 'The most influential technology leaders and their business empires',
    icon: 'Cpu',
    color: 'blue',
    industries: ['Technology', 'Semiconductors'],
    searchQuery: 'technology',
  },
  {
    key: 'finance',
    title: 'Financial Giants',
    description: 'Power players in banking, investment, and financial services',
    icon: 'DollarSign',
    color: 'emerald',
    industries: ['Finance'],
    searchQuery: 'finance',
  },
  {
    key: 'luxury',
    title: 'Luxury & Fashion',
    description: 'The empires behind the world\'s most coveted luxury brands',
    icon: 'Gem',
    color: 'purple',
    industries: ['Luxury Goods', 'Beauty', 'Apparel'],
    searchQuery: 'luxury',
  },
  {
    key: 'consumer',
    title: 'Consumer & Retail',
    description: 'Corporations shaping what the world eats, wears, and buys',
    icon: 'ShoppingBag',
    color: 'amber',
    industries: ['Consumer Goods', 'Food & Beverage', 'Retail'],
    searchQuery: 'consumer',
  },
];

const getColorClasses = (color) => {
  const map = {
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700', hover: 'group-hover:border-blue-300' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700', hover: 'group-hover:border-emerald-300' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-700', hover: 'group-hover:border-purple-300' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700', hover: 'group-hover:border-amber-300' },
  };
  return map[color] || map.blue;
};

const FeaturedSection = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState(
    categoryDefs.map((c) => ({ ...c, companyCount: null, topCompanies: [], totalRevenue: 0 }))
  );

  useEffect(() => {
    const fetchCategoryCounts = async () => {
      try {
        const updated = await Promise.all(
          categoryDefs.map(async (cat) => {
            // Fetch companies in these industries
            const { data: companies } = await supabase
              .from('companies')
              .select('id, name, revenue, logo_url')
              .in('industry', cat.industries)
              .order('revenue', { ascending: false })
              .limit(50);

            const totalRevenue = (companies || []).reduce((sum, c) => sum + (c.revenue || 0), 0);
            const topCompanies = (companies || []).slice(0, 4).map((c) => c.name);

            return {
              ...cat,
              companyCount: companies?.length || 0,
              topCompanies,
              totalRevenue,
            };
          })
        );
        setCategories(updated);
      } catch (err) {
        console.error('Category fetch error:', err);
      }
    };
    fetchCategoryCounts();
  }, []);

  const formatRevenue = (amount) => {
    if (!amount) return '$0';
    if (amount >= 1_000_000_000_000) return `$${(amount / 1_000_000_000_000).toFixed(1)}T`;
    if (amount >= 1_000_000_000) return `$${(amount / 1_000_000_000).toFixed(0)}B`;
    if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(0)}M`;
    return `$${amount.toLocaleString()}`;
  };

  return (
    <div className="py-16 px-4 bg-gray-50/60">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="font-heading font-bold text-2xl lg:text-3xl text-gray-900 mb-4">
            Explore by Sector
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Dive into specific industries — real companies, real revenue, real connections
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {categories.map((cat) => {
            const colors = getColorClasses(cat.color);
            return (
              <div
                key={cat.key}
                onClick={() => navigate(`/search-results?q=${cat.searchQuery}`)}
                className={`bg-white rounded-xl p-6 group cursor-pointer border border-gray-200 ${colors.hover} hover:shadow-lg transition-all duration-300`}
              >
                {/* Icon */}
                <div className={`w-14 h-14 mb-4 rounded-xl border-2 flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${colors.bg} ${colors.text} ${colors.border}`}>
                  <Icon name={cat.icon} size={24} />
                </div>

                {/* Title */}
                <h3 className="font-bold text-lg text-gray-900 mb-1.5">
                  {cat.title}
                </h3>
                <p className="text-sm text-gray-500 mb-4 line-clamp-2">
                  {cat.description}
                </p>

                {/* Live stats */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-center">
                    <div className="text-xl font-bold text-gray-900">
                      {cat.companyCount !== null ? cat.companyCount : '—'}
                    </div>
                    <div className="text-[10px] text-gray-400 uppercase tracking-wider">companies</div>
                  </div>
                  <div className="h-8 w-px bg-gray-200"></div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-gray-900">
                      {cat.totalRevenue ? formatRevenue(cat.totalRevenue) : '—'}
                    </div>
                    <div className="text-[10px] text-gray-400 uppercase tracking-wider">total revenue</div>
                  </div>
                </div>

                {/* Top companies */}
                {cat.topCompanies.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-4">
                    {cat.topCompanies.map((name, idx) => (
                      <span
                        key={idx}
                        className={`px-2 py-0.5 text-[10px] rounded-full font-medium ${colors.badge}`}
                      >
                        {name.length > 18 ? name.slice(0, 16) + '…' : name}
                      </span>
                    ))}
                  </div>
                )}

                {/* CTA */}
                <div className={`inline-flex items-center text-sm font-medium text-gray-400 transition-colors duration-200 group-hover:${colors.text.replace('text-', 'text-')}`}>
                  Explore {cat.title}
                  <Icon name="ArrowRight" size={14} className="ml-1 group-hover:translate-x-1 transition-transform duration-200" />
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center">
          <div className="bg-white border border-gray-200 rounded-xl p-8 max-w-2xl mx-auto">
            <h3 className="font-bold text-xl text-gray-900 mb-3">
              Browse All Profiles
            </h3>
            <p className="text-gray-500 mb-6">
              Search across every individual, company, and PAC in our database.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/search-results')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <Icon name="Search" size={16} />
                Explore All
              </button>
              <button
                onClick={() => navigate('/pac-directory')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gray-700 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-200"
              >
                <Icon name="Landmark" size={16} />
                PAC Directory
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturedSection;
